import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner, Alert } from 'react-bootstrap';
import {
  getServiceRadius,
  updateServiceRadius,
  clearError
} from '../features/serviceRadius/serviceRadiusSlice';
import '../css/ServiceArea.css';

const ServiceArea = () => {
  const dispatch = useDispatch();
  const { services, summary, isLoading, error } = useSelector((state) => state.serviceRadius);

  // Bases state - multiple bases support
  const [bases, setBases] = useState([
    {
      id: 1,
      name: 'Westwood Garage',
      address: '1234 Wilshire Blvd, Los Angeles, CA 90025',
      isExpanded: false,
      isNew: false
    },
    {
      id: 2,
      name: 'New Base',
      address: 'Enter Address',
      isExpanded: true,
      isNew: true
    }
  ]);

  const [expandedServices, setExpandedServices] = useState([]);
  const [localValues, setLocalValues] = useState({});
  const [editingField, setEditingField] = useState(null);

  // Fetch service radius on component mount
  useEffect(() => {
    dispatch(getServiceRadius());
  }, [dispatch]);

  // Sync local values with Redux state when services load
  useEffect(() => {
    if (services.length > 0) {
      const initialValues = {};
      services.forEach(service => {
        initialValues[service.ServiceId] = service.ServiceRadiusMiles;
      });
      setLocalValues(initialValues);
    }
  }, [services]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Toggle base expansion
  const toggleBaseExpansion = (baseId) => {
    setBases(prev => prev.map(base =>
      base.id === baseId ? { ...base, isExpanded: !base.isExpanded } : base
    ));
  };

  // Delete base
  const deleteBase = (baseId) => {
    setBases(prev => prev.filter(base => base.id !== baseId));
  };

  // Update base field
  const updateBaseField = (baseId, field, value) => {
    setBases(prev => prev.map(base =>
      base.id === baseId ? { ...base, [field]: value, isNew: false } : base
    ));
  };

  // Toggle service expansion
  const toggleServiceExpansion = (serviceId) => {
    if (expandedServices.includes(serviceId)) {
      setExpandedServices(prev => prev.filter(id => id !== serviceId));
    } else {
      setExpandedServices(prev => [...prev, serviceId]);
    }
  };

  // Handle slider change
  const handleSliderChange = (serviceId, newRadius) => {
    const numValue = parseFloat(newRadius);
    setLocalValues(prev => ({
      ...prev,
      [serviceId]: numValue
    }));
  };

  // Handle slider mouse up - save
  const handleSliderMouseUp = async (serviceId, newRadius) => {
    try {
      await dispatch(updateServiceRadius({
        ServiceId: serviceId,
        ServiceRadiusMiles: parseFloat(newRadius)
      })).unwrap();
    } catch (err) {
      setLocalValues(prev => ({
        ...prev,
        [serviceId]: services.find(s => s.ServiceId === serviceId)?.ServiceRadiusMiles || prev[serviceId]
      }));
    }
  };

  // Get min and max from summary or use defaults
  const minRange = summary?.RadiusConfig?.MinRadius || 5;
  const maxRange = summary?.RadiusConfig?.MaxRadius || 25;

  // Add new base
  const addNewBase = () => {
    const newId = bases.length > 0 ? Math.max(...bases.map(b => b.id)) + 1 : 1;
    setBases(prev => [...prev, {
      id: newId,
      name: 'New Base',
      address: 'Enter Address',
      isExpanded: true,
      isNew: true
    }]);
  };

  // Static sub-services data (will be replaced with API data)
  const subServicesData = {
    'Fuel Delivery': [
      { id: 1, name: 'Unleaded', description: 'Standard 87-octane petrol suitable for most vehicles.', isActive: true },
      { id: 2, name: 'Premium', description: 'Higher octane petrol (91-93) for performance engines.', isActive: false },
      { id: 3, name: 'Ultra-Low Sulfur Diesel', description: 'Cleaner-burning diesel suitable for commercial and private vehicles.', isActive: true }
    ]
  };

  return (
    <div className="service-area-page">
      <div className="service-area-container">
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <div className="service-area-header">
          <h1 className="service-area-title">Select Your Service Area</h1>
          <p className="service-area-subtitle">Set where you operate from. Add more bases if you serve from multiple locations</p>
        </div>

        {/* Bases List */}
        <div className="bases-list">
          {bases.map((base) => (
            <div key={base.id} className={`base-card ${base.isExpanded ? 'expanded' : ''}`}>
              {/* Base Header */}
              <div className="base-card-header">
                <div className="base-card-left">
                  <div className="base-location-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#6366F1"/>
                    </svg>
                  </div>
                  <div className="base-card-info">
                    <h3 className="base-card-name">{base.name}</h3>
                    <p className="base-card-address">{base.address}</p>
                  </div>
                </div>
                <div className="base-card-actions">
                  <button className="base-action-btn delete-btn" onClick={() => deleteBase(base.id)}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M2.5 5H4.16667H17.5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.66666 5V3.33333C6.66666 2.89131 6.84225 2.46738 7.15481 2.15482C7.46737 1.84226 7.8913 1.66667 8.33332 1.66667H11.6667C12.1087 1.66667 12.5326 1.84226 12.8452 2.15482C13.1577 2.46738 13.3333 2.89131 13.3333 3.33333V5M15.8333 5V16.6667C15.8333 17.1087 15.6577 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83332C5.3913 18.3333 4.96737 18.1577 4.65481 17.8452C4.34225 17.5326 4.16666 17.1087 4.16666 16.6667V5H15.8333Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className={`base-action-btn expand-btn ${base.isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleBaseExpansion(base.id)}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M5 12.5L10 7.5L15 12.5" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              {base.isExpanded && (
                <div className="base-card-content">
                  {/* Base Name & Address Inputs */}
                  <div className="base-inputs-row">
                    <div className="base-input-group">
                      <label className="base-input-label">Base Name</label>
                      <div className="base-input-field">
                        <svg className="base-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M2.5 7.5L10 2.5L17.5 7.5V16.6667C17.5 17.1087 17.3244 17.5326 17.0118 17.8452C16.6993 18.1577 16.2754 18.3333 15.8333 18.3333H4.16667C3.72464 18.3333 3.30072 18.1577 2.98816 17.8452C2.67559 17.5326 2.5 17.1087 2.5 16.6667V7.5Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M7.5 18.3333V10H12.5V18.3333" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                          type="text"
                          placeholder="e.g. Westwood Garage"
                          value={base.isNew ? '' : base.name}
                          onChange={(e) => updateBaseField(base.id, 'name', e.target.value)}
                          className="base-input"
                        />
                        <svg className="base-input-edit" width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M14.1667 2.49998C14.3856 2.28111 14.6454 2.10749 14.9314 1.98903C15.2173 1.87057 15.5238 1.80963 15.8333 1.80963C16.1429 1.80963 16.4494 1.87057 16.7353 1.98903C17.0213 2.10749 17.2811 2.28111 17.5 2.49998C17.7189 2.71886 17.8925 2.97869 18.011 3.26463C18.1294 3.55057 18.1904 3.85706 18.1904 4.16665C18.1904 4.47623 18.1294 4.78272 18.011 5.06866C17.8925 5.3546 17.7189 5.61443 17.5 5.83331L6.25001 17.0833L1.66667 18.3333L2.91667 13.75L14.1667 2.49998Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    <div className="base-input-group">
                      <label className="base-input-label">Address</label>
                      <div className="base-input-field">
                        <svg className="base-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 10.8333C11.3807 10.8333 12.5 9.71403 12.5 8.33331C12.5 6.9526 11.3807 5.83331 10 5.83331C8.61929 5.83331 7.5 6.9526 7.5 8.33331C7.5 9.71403 8.61929 10.8333 10 10.8333Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 18.3333C13.3333 15 16.6667 12.0152 16.6667 8.33331C16.6667 4.65141 13.6819 1.66665 10 1.66665C6.31811 1.66665 3.33334 4.65141 3.33334 8.33331C3.33334 12.0152 6.66668 15 10 18.3333Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                          type="text"
                          placeholder="Enter address"
                          value={base.isNew ? '' : base.address}
                          onChange={(e) => updateBaseField(base.id, 'address', e.target.value)}
                          className="base-input"
                        />
                        <svg className="base-input-edit" width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M14.1667 2.49998C14.3856 2.28111 14.6454 2.10749 14.9314 1.98903C15.2173 1.87057 15.5238 1.80963 15.8333 1.80963C16.1429 1.80963 16.4494 1.87057 16.7353 1.98903C17.0213 2.10749 17.2811 2.28111 17.5 2.49998C17.7189 2.71886 17.8925 2.97869 18.011 3.26463C18.1294 3.55057 18.1904 3.85706 18.1904 4.16665C18.1904 4.47623 18.1294 4.78272 18.011 5.06866C17.8925 5.3546 17.7189 5.61443 17.5 5.83331L6.25001 17.0833L1.66667 18.3333L2.91667 13.75L14.1667 2.49998Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Services List */}
                  {isLoading && services.length === 0 ? (
                    <div className="text-center my-4">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2" style={{ color: '#64748B' }}>Loading services...</p>
                    </div>
                  ) : services.length > 0 ? (
                    <div className="base-services-list">
                      {services.map((service) => (
                        <div key={service.ServiceId} className="base-service-card">
                          {/* Service Header */}
                          <div className="base-service-header">
                            <div className="base-service-info">
                              <h4 className="base-service-name">{service.ServiceName}</h4>
                              <p className="base-service-desc">{service.Description || 'Deliver fuel to stranded or remote vehicles quickly.'}</p>
                            </div>
                            <div className="base-service-controls">
                              <div className="base-service-slider-wrapper">
                                <input
                                  type="range"
                                  min={minRange}
                                  max={maxRange}
                                  value={localValues[service.ServiceId] || service.ServiceRadiusMiles}
                                  onChange={(e) => handleSliderChange(service.ServiceId, e.target.value)}
                                  onMouseUp={(e) => handleSliderMouseUp(service.ServiceId, e.target.value)}
                                  onTouchEnd={(e) => handleSliderMouseUp(service.ServiceId, e.target.value)}
                                  className="base-service-slider"
                                  style={{
                                    background: `linear-gradient(to right, #6366F1 0%, #6366F1 ${(((localValues[service.ServiceId] || service.ServiceRadiusMiles) - minRange) / (maxRange - minRange)) * 100}%, #E0E7FF ${(((localValues[service.ServiceId] || service.ServiceRadiusMiles) - minRange) / (maxRange - minRange)) * 100}%, #E0E7FF 100%)`
                                  }}
                                />
                              </div>
                              <span className="base-service-miles">{localValues[service.ServiceId] || service.ServiceRadiusMiles} miles</span>
                              <label className="base-service-toggle">
                                <input type="checkbox" defaultChecked />
                                <span className="toggle-slider"></span>
                              </label>
                              <button
                                className={`base-service-expand-btn ${expandedServices.includes(service.ServiceId) ? 'expanded' : ''}`}
                                onClick={() => toggleServiceExpansion(service.ServiceId)}
                              >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path d="M5 12.5L10 7.5L15 12.5" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Sub-services */}
                          {expandedServices.includes(service.ServiceId) && (
                            <div className="base-subservices-list">
                              {(subServicesData[service.ServiceName] || []).map((subService) => (
                                <div key={subService.id} className="base-subservice-item">
                                  <div className="base-subservice-info">
                                    <h5 className="base-subservice-name">{subService.name}</h5>
                                    <p className="base-subservice-desc">{subService.description}</p>
                                  </div>
                                  <label className="base-service-toggle">
                                    <input type="checkbox" defaultChecked={subService.isActive} />
                                    <span className="toggle-slider"></span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center my-4" style={{ color: '#64748B' }}>
                      No services available
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="base-action-buttons">
                    <button className="base-cancel-btn" onClick={() => toggleBaseExpansion(base.id)}>
                      CANCEL
                    </button>
                    <button className="base-save-btn">
                      SAVE
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add Another Base Location Button */}
          <button className="add-base-btn" onClick={addNewBase}>
            <span className="add-base-icon">+</span>
            <span className="add-base-text">Add Another Base Location</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceArea;
